import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, Download, Receipt } from "@phosphor-icons/react";

const FONT_STACK =
  "'Helvetica Neue', Helvetica, Arial, ui-sans-serif, -apple-system, BlinkMacSystemFont, sans-serif";

const LOGO_URL = "/assets/rodbez-logo.jpg";

// ─────────────────────────────────────────────────────────────────────────────
// Demo data — wire to backend later. Spellings of FIXED labels are corrected
// from the original PDF (Floor / Complex / Bailey / Helpline / Convenience).
// Backend gives us GST-INCLUSIVE totals; component reverse-derives base + tax.
// ─────────────────────────────────────────────────────────────────────────────
const DEMO_OWN_FLEET = {
  type: "OWN_FLEET",
  invoiceId: "RB313232233",
  invoiceDate: "02 Jan 2024",
  gstin: "10AAJCD3192L1ZN",
  sacCode: "996412",
  category: "Renting of motor cab",
  customer: {
    name: "Dilkhush Kumar",
    mobile: "+919155394055",
    placeOfSupply: "Bihar",
  },
  pickupLocation: "4th Floor Rajmati Complex, Bailey Road Saguna More Patna 801503",
  dropLocation: "4th Floor Rajmati Complex, Bailey Road Saguna More Patna 801503",
  fare: {
    totalRideFare: 1050.0, // INCLUSIVE of CGST+SGST
    cgstPercent: 2.5,
    sgstPercent: 2.5,
  },
};

const DEMO_CONVENIENCE = {
  type: "CONVENIENCE_FEE",

  // ── Page 1 — Ride Summary ──
  summary: {
    rideNumber: "RB012345678",
    customerName: "Dilkhush Kumar",
    rideType: "One Way Taxi",
    travelDistance: "370 KM",
    travelTime: "10:30 Hr",
    cabClass: "Sedan",
    from: "Patna India Bihar",
    to: "Saharsa Bihar India",
    pickupDateTime: "17 Jan 2023, 10:30 AM",
    dropDateTime: "17 Jan 2023, 12:30 PM",
    yourTrip: 1168.0,
    totalBillRounded: 1168,
    taxesIncluded: 25.25,
  },

  // ── Page 2 — Convenience Fee Tax Invoice ──
  conv: {
    invoiceId: "RB313232233",
    invoiceDate: "02 Jan 2024",
    gstin: "10AAJCD3192L1ZN",
    sacCode: "999799",
    category: "Business Auxiliary Service",
    customer: {
      name: "Dilkhush Kumar",
      mobile: "+919155394055",
      placeOfSupply: "Bihar",
    },
    pickupLocation: "4th Floor Rajmati Complex, Bailey Road Saguna More Patna 801503",
    dropLocation: "4th Floor Rajmati Complex, Bailey Road Saguna More Patna 801503",
    fare: {
      totalConvenienceFee: 118.0, // INCLUSIVE of CGST+IGST
      cgstPercent: 9,
      igstPercent: 9,
    },
  },

  // ── Page 3 — Driver Trip Tax Invoice ──
  driver: {
    invoiceId: "RBDR00000123",
    invoiceDate: "13/12/2022",
    driverName: "Rajesh Kumar",
    fatherName: "Sanjeev Singh",
    address: "Andama Darbhanga Bihar India",
    serviceTaxCategory: "Renting of motor cab",
    sacCode: "996412",
    cabType: "Dzire Tour CNG",
    rideType: "One-Way Trip",
    customer: {
      name: "Dilkhush Kumar",
      mobile: "+917488296076",
      pickupAddress: "Brangan Saharsa Bihar 852212",
    },
    distance: "140 KM",
    duration: "2:30 Hr",
    fare: {
      customerRideNumber: "RB012345678",
      totalRideFare: 1050.0, // INCLUSIVE of CGST+SGST
      cgstPercent: 2.5,
      sgstPercent: 2.5,
    },
  },
};

// ── Constants shared across all sheets ──
const COMPANY = {
  legalName: "Difelly Mobility India Private Limited",
  corpOffice:
    "Corporate Office - 3rd to 6th Floor Rajmati Complex, Bailey Road Saguna More Patna 801503",
  regOffice: "C/O Pawan Khan, Bangaon Kahara, Saharsa Bihar 852212",
  helpline: "+919155394055",
  website: "www.rodbez.in",
  careEmail: "care@rodbez.in",
};

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────
const fmt = (n) =>
  `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtRound = (n) =>
  `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

// Reverse-calculate base + per-component tax from a GST-inclusive total.
const splitInclusive = (totalInclusive, taxPercents) => {
  const total = Number(totalInclusive || 0);
  const sumPct = taxPercents.reduce((a, b) => a + Number(b || 0), 0);
  const base = total / (1 + sumPct / 100);
  const round2 = (x) => Math.round(x * 100) / 100;
  const baseR = round2(base);
  const taxes = taxPercents.map((p) => round2((base * p) / 100));
  const sumSoFar = round2(baseR + taxes.slice(0, -1).reduce((a, b) => a + b, 0));
  taxes[taxes.length - 1] = round2(total - sumSoFar);
  return { base: baseR, taxes };
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared building blocks (PDF-style minimalist)
// ─────────────────────────────────────────────────────────────────────────────
const Sheet = ({ children, dataTestId }) => (
  <div
    data-testid={dataTestId}
    className="invoice-sheet bg-white text-black mx-auto shadow-sm"
    style={{
      width: 794,
      minHeight: 1123,
      padding: "44px 48px 36px",
      fontFamily: FONT_STACK,
      fontSize: 12,
      lineHeight: 1.45,
      color: "#1a1a1a",
    }}
  >
    {children}
  </div>
);

const Logo = ({ size = 56 }) => (
  <img
    src={LOGO_URL}
    alt="RodBez"
    style={{
      height: size,
      width: "auto",
      maxWidth: size * 1.6,
      objectFit: "contain",
      display: "block",
    }}
  />
);

const Hr = ({ dashed = false, color = "#1a1a1a", thickness = 1 }) => (
  <hr
    style={{
      border: 0,
      borderTop: `${thickness}px ${dashed ? "dashed" : "solid"} ${color}`,
      margin: 0,
    }}
  />
);

const FieldLine = ({ label, value, valueBold = true }) => (
  <div className="flex items-baseline gap-2 py-[3px]">
    <span style={{ color: "#555", fontSize: 11.5 }}>{label}</span>
    <span style={{ fontWeight: valueBold ? 600 : 400, fontSize: 12 }}>{value || "—"}</span>
  </div>
);

// Two-column field row (label-value pair on each side)
const KVPair = ({ label, value, alignRight = false }) => (
  <div className={`flex items-baseline gap-2 ${alignRight ? "justify-end" : ""}`}>
    <span style={{ color: "#555", fontSize: 11.5 }}>{label}</span>
    <span style={{ fontWeight: 600, fontSize: 12 }}>{value}</span>
  </div>
);

// Description / Amount table — black header, plain body, divider before total.
const ChargeTable = ({ rows, totalLabel, totalAmount }) => (
  <table
    className="w-full"
    style={{ borderCollapse: "collapse", marginTop: 14, fontSize: 12 }}
  >
    <thead>
      <tr style={{ background: "#000", color: "#fff" }}>
        <th
          style={{
            textAlign: "left",
            padding: "8px 12px",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          Description
        </th>
        <th
          style={{
            textAlign: "right",
            padding: "8px 12px",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          Amount (INR)
        </th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i}>
          <td
            style={{
              padding: "7px 12px",
              borderBottom: "1px solid #e5e5e5",
              color: r.muted ? "#555" : "#1a1a1a",
            }}
          >
            {r.label}
          </td>
          <td
            style={{
              padding: "7px 12px",
              borderBottom: "1px solid #e5e5e5",
              textAlign: "right",
              color: r.muted ? "#555" : "#1a1a1a",
            }}
          >
            {r.value}
          </td>
        </tr>
      ))}
      <tr>
        <td
          style={{
            padding: "10px 12px",
            fontWeight: 700,
            borderTop: "2px solid #000",
            borderBottom: "2px solid #000",
          }}
        >
          {totalLabel}
        </td>
        <td
          style={{
            padding: "10px 12px",
            fontWeight: 700,
            textAlign: "right",
            fontSize: 13,
            borderTop: "2px solid #000",
            borderBottom: "2px solid #000",
          }}
        >
          {totalAmount}
        </td>
      </tr>
    </tbody>
  </table>
);

// Footer block used on tax-invoice sheets.
const InvoiceFooter = ({ disclaimers = [] }) => (
  <div style={{ marginTop: 22, fontSize: 10.5, color: "#333", lineHeight: 1.55 }}>
    {disclaimers.map((d, i) => (
      <div key={i} style={{ marginBottom: 6 }}>
        <strong style={{ color: "#000" }}>{i + 1}.</strong> {d}
      </div>
    ))}
    <div style={{ marginTop: 10 }}>
      <strong style={{ color: "#000" }}>Reg. Office:</strong> {COMPANY.regOffice}
    </div>
    <div style={{ marginTop: 2 }}>
      <strong style={{ color: "#000" }}>Helpline Number:</strong> {COMPANY.helpline}
      {",  "}
      <strong style={{ color: "#000" }}>Website:</strong> {COMPANY.website}
    </div>
  </div>
);

// Header used on Own Fleet + Convenience-Page-2 (logo + company + Tax Invoice title).
const TaxInvoiceHeader = ({ title = "Original Tax Invoice" }) => (
  <>
    <div className="flex items-start justify-between gap-6">
      <div className="flex items-start gap-3">
        <Logo size={56} />
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{COMPANY.legalName}</div>
          <div style={{ fontSize: 11, color: "#444", maxWidth: 360, marginTop: 2 }}>
            {COMPANY.corpOffice}
          </div>
        </div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 10.5, letterSpacing: 1.5, color: "#666" }}>
          TAX INVOICE
        </div>
        <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>{title}</div>
      </div>
    </div>
    <div style={{ marginTop: 14 }}>
      <Hr thickness={1.5} />
    </div>
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
// SHEET 1 (Own Fleet) — single page tax invoice
// ─────────────────────────────────────────────────────────────────────────────
function OwnFleetSheet({ data }) {
  const { base, taxes } = splitInclusive(data.fare.totalRideFare, [
    data.fare.cgstPercent,
    data.fare.sgstPercent,
  ]);

  return (
    <Sheet dataTestId="invoice-sheet-own-fleet">
      <TaxInvoiceHeader />

      {/* Two-column meta */}
      <div className="grid grid-cols-2 gap-x-10" style={{ marginTop: 16 }}>
        <div>
          <FieldLine label="GSTIN:" value={data.gstin} />
          <FieldLine label="SAC Code:" value={data.sacCode} />
          <FieldLine label="Category" value={data.category} />
        </div>
        <div>
          <FieldLine label="Invoice ID" value={data.invoiceId} />
          <FieldLine label="Invoice Date" value={data.invoiceDate} />
        </div>
      </div>

      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <Hr dashed color="#bbb" />
      </div>

      <div className="grid grid-cols-2 gap-x-10">
        <FieldLine label="Customer Name:" value={data.customer.name} />
        <FieldLine label="Mobile Number" value={data.customer.mobile} />
      </div>
      <FieldLine label="Place of Supply State:" value={data.customer.placeOfSupply} />

      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <Hr dashed color="#bbb" />
      </div>

      <div>
        <FieldLine label="Pickup Location:" value={data.pickupLocation} />
        <FieldLine label="Drop Location:" value={data.dropLocation} />
      </div>

      <ChargeTable
        rows={[
          { label: "Ride Fare", value: fmt(base) },
          {
            label: `CGST (${data.fare.cgstPercent}%)`,
            value: fmt(taxes[0]),
          },
          {
            label: `SGST (${data.fare.sgstPercent}%)`,
            value: fmt(taxes[1]),
          },
        ]}
        totalLabel="Total (Ride Fare)"
        totalAmount={fmt(data.fare.totalRideFare)}
      />

      <InvoiceFooter
        disclaimers={[
          "This is an electronically generated invoice and does not require a digital signature.",
        ]}
      />
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SHEET 2 (Convenience — Page 1) — Ride Summary
// ─────────────────────────────────────────────────────────────────────────────
function RideSummarySheet({ data }) {
  const s = data.summary;
  return (
    <Sheet dataTestId="invoice-sheet-conv-summary">
      {/* Header — total left, logo right */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <div style={{ fontSize: 11, color: "#666", letterSpacing: 1.2 }}>
            TOTAL BILL
          </div>
          <div
            style={{
              fontSize: 44,
              fontWeight: 800,
              lineHeight: 1.1,
              marginTop: 4,
              letterSpacing: -0.5,
            }}
          >
            {fmtRound(s.totalBillRounded)}
          </div>
          <div style={{ marginTop: 6, color: "#666", fontSize: 11.5 }}>
            ── {s.rideNumber} ──
          </div>
          <div style={{ marginTop: 14, fontSize: 13 }}>
            Thanks for travelling with us, <strong>{s.customerName}</strong>
          </div>
        </div>
        <Logo size={72} />
      </div>

      <div style={{ marginTop: 24, marginBottom: 14 }}>
        <Hr color="#d4d4d4" />
      </div>

      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
        Ride Details
      </div>

      <div className="grid grid-cols-2 gap-x-10 gap-y-2" style={{ fontSize: 12 }}>
        <KVPair label="Ride Type-" value={s.rideType} />
        <KVPair label="Cab Class-" value={s.cabClass} />
        <KVPair label="Travel Distance-" value={s.travelDistance} />
        <KVPair label="Travel Time-" value={s.travelTime} />
        <KVPair label="From-" value={s.from} />
        <KVPair label="To-" value={s.to} />
        <KVPair label="Pickup Date & Time-" value={s.pickupDateTime} />
        <KVPair label="Drop Date & Time-" value={s.dropDateTime} />
      </div>

      <div style={{ marginTop: 22, marginBottom: 14 }}>
        <Hr color="#d4d4d4" />
      </div>

      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>
        Bill Details
      </div>

      <div className="flex justify-between" style={{ padding: "6px 0", fontSize: 12 }}>
        <span>Your Trip</span>
        <span style={{ fontWeight: 600 }}>{fmt(s.yourTrip)}</span>
      </div>

      <div
        className="flex justify-between items-baseline"
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: "2px solid #000",
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Total Bill (rounded)</div>
          <div style={{ fontSize: 10.5, color: "#666", marginTop: 2 }}>
            Includes ₹{Number(s.taxesIncluded).toFixed(2)} Taxes
          </div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 800 }}>{fmtRound(s.totalBillRounded)}</div>
      </div>

      {/* Footer — grievance block */}
      <div style={{ marginTop: "auto", paddingTop: 36 }}>
        <div style={{ fontSize: 11, color: "#333" }}>
          In case of any complaint/grievance against this invoice, write to us at
        </div>
        <div style={{ fontSize: 11, color: "#333", marginTop: 4 }}>
          <strong style={{ color: "#000" }}>Grievance officer,</strong>{" "}
          {COMPANY.legalName} (RodBez), {COMPANY.corpOffice}
        </div>
        <div style={{ fontSize: 11, color: "#333", marginTop: 6 }}>
          <strong style={{ color: "#000" }}>Have any queries?</strong> Email us{" "}
          <a href={`mailto:${COMPANY.careEmail}`} style={{ color: "#0a66c2" }}>
            {COMPANY.careEmail}
          </a>
          , or call us {COMPANY.helpline}
        </div>
      </div>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SHEET 3 (Convenience — Page 2) — Convenience Fee tax invoice
// ─────────────────────────────────────────────────────────────────────────────
function ConvenienceFeeSheet({ conv }) {
  const { base, taxes } = splitInclusive(conv.fare.totalConvenienceFee, [
    conv.fare.cgstPercent,
    conv.fare.igstPercent,
  ]);

  return (
    <Sheet dataTestId="invoice-sheet-conv-fee">
      <TaxInvoiceHeader />

      <div className="grid grid-cols-2 gap-x-10" style={{ marginTop: 16 }}>
        <div>
          <FieldLine label="GSTIN:" value={conv.gstin} />
          <FieldLine label="SAC Code:" value={conv.sacCode} />
          <FieldLine label="Category" value={conv.category} />
        </div>
        <div>
          <FieldLine label="Invoice ID" value={conv.invoiceId} />
          <FieldLine label="Invoice Date" value={conv.invoiceDate} />
        </div>
      </div>

      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <Hr dashed color="#bbb" />
      </div>

      <div className="grid grid-cols-2 gap-x-10">
        <FieldLine label="Customer Name:" value={conv.customer.name} />
        <FieldLine label="Mobile Number" value={conv.customer.mobile} />
      </div>
      <FieldLine label="Place of Supply State:" value={conv.customer.placeOfSupply} />

      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <Hr dashed color="#bbb" />
      </div>

      <FieldLine label="Pickup Location:" value={conv.pickupLocation} />
      <FieldLine label="Drop Location:" value={conv.dropLocation} />

      <ChargeTable
        rows={[
          { label: "Convenience Fee", value: fmt(base) },
          {
            label: `CGST (${conv.fare.cgstPercent}%)`,
            value: fmt(taxes[0]),
          },
          {
            label: `IGST (${conv.fare.igstPercent}%)`,
            value: fmt(taxes[1]),
          },
        ]}
        totalLabel="Total (Convenience Fee)"
        totalAmount={fmt(conv.fare.totalConvenienceFee)}
      />

      <InvoiceFooter
        disclaimers={[
          "This is an electronically generated invoice and does not require a digital signature.",
        ]}
      />
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SHEET 4 (Convenience — Page 3) — Driver Trip tax invoice
// ─────────────────────────────────────────────────────────────────────────────
function DriverTripSheet({ driver }) {
  const { base, taxes } = splitInclusive(driver.fare.totalRideFare, [
    driver.fare.cgstPercent,
    driver.fare.sgstPercent,
  ]);

  return (
    <Sheet dataTestId="invoice-sheet-driver-trip">
      {/* This sheet has no logo — title only (per source PDF) */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <div style={{ fontSize: 10.5, letterSpacing: 1.5, color: "#666" }}>
            DRIVER TRIP INVOICE
          </div>
          <div style={{ fontWeight: 700, fontSize: 14, marginTop: 4 }}>
            {driver.driverName}
          </div>
          <div style={{ fontSize: 12, color: "#333" }}>
            S/O {driver.fatherName}
          </div>
          <div style={{ fontSize: 12, color: "#333" }}>{driver.address}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10.5, letterSpacing: 1.5, color: "#666" }}>
            TAX INVOICE
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, marginTop: 2 }}>
            Original Tax Invoice
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <Hr thickness={1.5} />
      </div>

      <div className="grid grid-cols-2 gap-x-10" style={{ marginTop: 16 }}>
        <div>
          <FieldLine
            label="Service Tax Category:"
            value={driver.serviceTaxCategory}
          />
          <FieldLine label="SAC Code:" value={driver.sacCode} />
          <FieldLine label="Cab Type-" value={driver.cabType} />
          <FieldLine label="Ride Type" value={driver.rideType} />
        </div>
        <div>
          <FieldLine label="Invoice ID" value={driver.invoiceId} />
          <FieldLine label="Invoice Date-" value={driver.invoiceDate} />
        </div>
      </div>

      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <Hr dashed color="#bbb" />
      </div>

      <div className="grid grid-cols-2 gap-x-10">
        <FieldLine label="Customer Name" value={driver.customer.name} />
        <FieldLine label="Mobile Number" value={driver.customer.mobile} />
      </div>
      <FieldLine label="Pickup Address" value={driver.customer.pickupAddress} />

      <div className="grid grid-cols-2 gap-x-10" style={{ marginTop: 8 }}>
        <FieldLine label="Total Travel Distance" value={driver.distance} />
        <FieldLine label="Total Travel Time" value={driver.duration} />
      </div>

      <div style={{ marginTop: 14 }}>
        <FieldLine
          label="Customer Ride Number -"
          value={driver.fare.customerRideNumber}
        />
      </div>

      <ChargeTable
        rows={[
          { label: "Ride Fare", value: fmt(base) },
          {
            label: `CGST (${driver.fare.cgstPercent}%)`,
            value: fmt(taxes[0]),
          },
          {
            label: `SGST (${driver.fare.sgstPercent}%)`,
            value: fmt(taxes[1]),
          },
        ]}
        totalLabel="Total (Ride Fare)"
        totalAmount={fmt(driver.fare.totalRideFare)}
      />

      <InvoiceFooter
        disclaimers={[
          "This invoice is issued on behalf of the Transport Service Provider. Difelly Mobility India Private Limited acts in the capacity of an Electronic Commerce Operator as per Section 9(5) of the Central Goods & Service Tax Act, 2017 & corresponding Section 5(5) of the IGST Act, 2017.",
          "This is an electronically generated invoice and does not require a digital signature.",
        ]}
      />
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page wrapper
// ─────────────────────────────────────────────────────────────────────────────
export default function Invoice() {
  const navigate = useNavigate();
  const [variant, setVariant] = useState("OWN_FLEET");

  const handlePrint = () => window.print();

  return (
    <div className="min-h-screen" data-testid="invoice-page" style={{ background: "#eef0f3" }}>
      {/* Print rules — A4, no chrome, page-break per sheet */}
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          html, body { background: #fff !important; }
          .no-print { display: none !important; }
          .invoice-canvas { padding: 0 !important; gap: 0 !important; }
          .invoice-sheet {
            box-shadow: none !important;
            margin: 0 auto !important;
            page-break-after: always;
            break-after: page;
          }
          .invoice-sheet:last-child {
            page-break-after: auto;
            break-after: auto;
          }
        }
      `}</style>

      {/* Toolbar */}
      <header className="no-print sticky top-0 z-10 bg-white border-b border-[#e5e7eb]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            data-testid="invoice-back-btn"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-2 min-w-0">
            <Receipt size={20} weight="duotone" className="text-black" />
            <div className="text-base font-semibold text-black heading">Invoice Preview</div>
            <span className="text-[10px] uppercase tracking-wide bg-[#f1f5f9] text-[#64748b] px-2 py-0.5 rounded-full">
              static demo
            </span>
            {variant === "CONVENIENCE_FEE" && (
              <span className="text-[10px] uppercase tracking-wide bg-black text-white px-2 py-0.5 rounded-full">
                3 pages
              </span>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div
              className="inline-flex rounded-md border border-[#cbd5e1] overflow-hidden"
              data-testid="invoice-variant-toggle"
            >
              <button
                onClick={() => setVariant("OWN_FLEET")}
                className={`px-3 py-1.5 text-xs font-medium ${
                  variant === "OWN_FLEET"
                    ? "bg-black text-white"
                    : "bg-white text-black hover:bg-[#f1f5f9]"
                }`}
                data-testid="invoice-variant-own-fleet"
              >
                Own Fleet (1 pg)
              </button>
              <button
                onClick={() => setVariant("CONVENIENCE_FEE")}
                className={`px-3 py-1.5 text-xs font-medium border-l border-[#cbd5e1] ${
                  variant === "CONVENIENCE_FEE"
                    ? "bg-black text-white"
                    : "bg-white text-black hover:bg-[#f1f5f9]"
                }`}
                data-testid="invoice-variant-convenience"
              >
                Convenience Fee (3 pg)
              </button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="h-9 gap-1.5"
              data-testid="invoice-print-btn"
            >
              <Printer size={14} /> Print
            </Button>
            <Button
              size="sm"
              onClick={handlePrint}
              className="h-9 gap-1.5 bg-black hover:bg-[#1a1a1a] text-white"
              data-testid="invoice-download-btn"
            >
              <Download size={14} /> Download PDF
            </Button>
          </div>
        </div>
      </header>

      {/* Sheets canvas */}
      <main className="invoice-canvas py-8 flex flex-col items-center gap-8">
        {variant === "OWN_FLEET" ? (
          <OwnFleetSheet data={DEMO_OWN_FLEET} />
        ) : (
          <>
            <RideSummarySheet data={DEMO_CONVENIENCE} />
            <ConvenienceFeeSheet conv={DEMO_CONVENIENCE.conv} />
            <DriverTripSheet driver={DEMO_CONVENIENCE.driver} />
          </>
        )}

        <div className="no-print mt-2 max-w-3xl mx-auto rounded-md border border-[#cbd5e1] bg-white p-4 text-[12px] text-[#475569] space-y-1">
          <div className="font-semibold text-black">Notes</div>
          <div>
            • <strong>Own Fleet</strong> = 1 page tax invoice (CGST + SGST 2.5% each).
          </div>
          <div>
            • <strong>Convenience Fee</strong> = 3 pages → Ride Summary → Convenience
            Fee tax invoice (CGST + IGST 9%) → Driver Trip tax invoice (CGST + SGST 2.5%).
          </div>
          <div>
            • Print/Download → "Save as PDF" me har sheet alag A4 page banta hai
            (page-break enforced).
          </div>
          <div>
            • Backend humein <strong>Total (inclusive of GST)</strong> dega; component
            khud base + tax breakdown nikaalta hai.
          </div>
        </div>
      </main>
    </div>
  );
}
